import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {alpha, type Theme, useTheme} from "@mui/material/styles";
import type {Audience} from "crqlar-react-common/dist/models/audience/audience.model";
import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import type {MarketingLifecyclePlayV4} from "../../models/marketing-v4";

const PLAY_ICONS: Record<string, ReactNode> = {
    topSpenders: <DiamondOutlinedIcon sx={{fontSize: 14}}/>,
    champions: <EmojiEventsOutlinedIcon sx={{fontSize: 14}}/>,
    firstTimers: <PersonAddAltOutlinedIcon sx={{fontSize: 14}}/>,
    rebookRepeat: <EventRepeatOutlinedIcon sx={{fontSize: 14}}/>,
    winBackValuable: <ReplayOutlinedIcon sx={{fontSize: 14}}/>,
    seasonalReturners: <WbSunnyOutlinedIcon sx={{fontSize: 14}}/>,
};

function toneColor(theme: Theme, tone: string): string {
    switch (tone) {
        case "success":
            return theme.palette.success.main;
        case "warning":
            return theme.palette.warning.main;
        case "danger":
            return theme.palette.error.main;
        case "primary":
            return theme.palette.primary.main;
        default:
            return theme.palette.info.main;
    }
}

interface LoadingLifecycleAudienceCardProps {
    loading: true;
}

interface LoadedLifecycleAudienceCardProps {
    play: MarketingLifecyclePlayV4;
    loading: false;
    currency: string;
    readOnly: boolean;
    onCreateAudience: (audience: Audience) => void;
}

type LifecycleAudienceCardProps = LoadingLifecycleAudienceCardProps | LoadedLifecycleAudienceCardProps;

export function LifecycleAudienceCard(props: LifecycleAudienceCardProps) {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const locale = i18n.resolvedLanguage || i18n.language;
    const numberFormatter = new Intl.NumberFormat(locale);
    let content: ReactNode;

    if (props.loading) {
        content = (
            <Stack sx={{height: "100%"}}>
                <Skeleton animation="wave" variant="rounded" width={76} height={22}/>
                <Skeleton animation="wave" width="68%" height={24} sx={{mt: 1.25}}/>
                <Skeleton animation="wave" width="86%" height={20}/>
                <Stack direction="row" alignItems="center" gap={1} sx={{mt: 1.5}}>
                    <Skeleton animation="wave" variant="rounded" width={92} height={28}/>
                    <Skeleton animation="wave" width={80} height={20}/>
                </Stack>
                <Skeleton animation="wave" width={142} height={20} sx={{mt: 0.5}}/>
                <Skeleton animation="wave" width={108} height={22} sx={{mt: "auto"}}/>
            </Stack>
        );
    } else {
        const {play, currency, readOnly, onCreateAudience} = props;
        const color = toneColor(theme, play.tone);
        const valueFormatter = new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            notation: "compact",
            maximumFractionDigits: 0,
        });
        const hasValueAtStake = play.valueAtStake > 0;

        content = (
            <>
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                        alignSelf: "flex-start",
                        minHeight: 22,
                        color,
                        bgcolor: alpha(color, 0.12),
                        px: 0.75,
                        borderRadius: 1,
                    }}
                >
                    {PLAY_ICONS[play.key] ?? <EmojiEventsOutlinedIcon sx={{fontSize: 14}}/>}
                    <Typography variant="caption" component="span" fontWeight={600} lineHeight={1}>
                        {t(play.tag)}
                    </Typography>
                </Stack>

                <Box sx={{mt: 1.25, minWidth: 0}}>
                    <Typography variant="body1" fontWeight={600} lineHeight={1.4}>
                        {t(play.label)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {play.description}
                    </Typography>
                </Box>

                <Box sx={{mt: 1.5}}>
                    <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
                        <Typography variant="h5" component="p" fontSize={24} fontWeight={700} lineHeight={1.15}>
                            {hasValueAtStake
                                ? valueFormatter.format(play.valueAtStake)
                                : numberFormatter.format(play.guests)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t(hasValueAtStake ? play.valueLabel : "guests")}
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                        {hasValueAtStake && `${numberFormatter.format(play.guests)} ${t("guests")} · `}
                        {numberFormatter.format(play.marketableGuests)} {t("Marketable")}
                        {play.marketablePct != null ? ` · ${play.marketablePct.toFixed(0)}%` : ""}
                    </Typography>
                </Box>

                <Box sx={{mt: "auto", pt: 2}}>
                    <Button
                        variant="text"
                        size="small"
                        endIcon={<ArrowForwardRoundedIcon sx={{fontSize: 16}}/>}
                        disabled={readOnly}
                        onClick={() => onCreateAudience(play.audience)}
                        sx={{
                            px: 0,
                            minWidth: 0,
                            textTransform: "none",
                            fontSize: 13,
                            color,
                            "&:hover": {bgcolor: "transparent", textDecoration: "underline"},
                        }}
                    >
                        {t("Create audience")}
                    </Button>
                </Box>
            </>
        );
    }

    return (
        <Paper
            component="article"
            variant="outlined"
            aria-busy={props.loading}
            sx={{
                width: "100%",
                minWidth: 0,
                minHeight: 210,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                px: {xs: 2, sm: 2.5},
                py: 2,
                borderRadius: 3,
                bgcolor: "background.paper",
                boxSizing: "border-box",
            }}
        >
            {content}
        </Paper>
    );
}

export default LifecycleAudienceCard;
